import instantsearch from 'instantsearch.js'
import { connectHits } from 'instantsearch.js/es/connectors'
import { history as historyRouter } from 'instantsearch.js/es/lib/routers'
import {
    configure,
    index,
    pagination,
    searchBox,
} from 'instantsearch.js/es/widgets'
import { getSearchClient, getSearchConfig } from './client.js'
import { craftSearchUrl, createGuardedSearchClient, probeEmptyIndex } from './guard.js'
import {
    buildSearchResultTracking,
    trackResultClick,
} from './insights.js'

const DEFAULT_RETRIEVE = [
    'objectID', 'kind', 'subtype', 'title', 'slug', 'url', 'access',
    'isCollection', 'collectionType', 'resourceType', 'summary', 'image',
    'fileExtension', 'fileSize', 'productTypes', 'productTypeLabels',
    'primaryProductType', 'sports', 'sportLabels', 'configurable',
    'publishedAt', 'updatedAt',
]

const PRODUCTS_RETRIEVE = [...DEFAULT_RETRIEVE, 'startingPrice']

const SECTIONS = [
    {
        id: 'productTypeCategories',
        label: 'Product Type Categories',
        filter: 'kind:page AND isCollection:true AND collectionType:productType',
        restrictSearchableAttributes: ['title'],
        hitsPerPage: 6,
        focusedHitsPerPage: 24,
        retrieve: DEFAULT_RETRIEVE,
        product: false,
        focused: true,
        card: 'menu',
    },
    {
        id: 'sports',
        label: 'Sports',
        filter: 'kind:page AND isCollection:true AND collectionType:sport',
        restrictSearchableAttributes: ['title'],
        hitsPerPage: 6,
        focusedHitsPerPage: 24,
        retrieve: DEFAULT_RETRIEVE,
        product: false,
        focused: true,
        card: 'sport',
    },
    {
        id: 'products',
        label: 'Products',
        filter: 'kind:product',
        hitsPerPage: 8,
        focusedHitsPerPage: 24,
        retrieve: PRODUCTS_RETRIEVE,
        product: true,
        focused: true,
        card: 'product',
    },
    {
        id: 'resources',
        label: 'Resources',
        filter: 'kind:resource',
        hitsPerPage: 6,
        focusedHitsPerPage: 24,
        retrieve: DEFAULT_RETRIEVE,
        product: false,
        focused: true,
        card: 'content',
    },
    {
        id: 'projects',
        label: 'Projects',
        filter: 'kind:project',
        hitsPerPage: 4,
        focusedHitsPerPage: 24,
        retrieve: DEFAULT_RETRIEVE,
        product: false,
        focused: true,
        card: 'content',
    },
    {
        id: 'pages',
        label: 'Pages',
        filter: 'kind:page AND isCollection:false',
        hitsPerPage: 4,
        focusedHitsPerPage: 24,
        retrieve: DEFAULT_RETRIEVE,
        product: false,
        focused: true,
        card: 'content',
    },
]

const FOCUSED_BY_ID = Object.fromEntries(
    SECTIONS.filter(section => section.focused).map(section => [section.id, section]),
)

const config = getSearchConfig()
const baseClient = getSearchClient()
const pageInput = document.querySelector('[data-search-page-input]')
const root = document.querySelector('[data-instantsearch-root]')
const metaEl = document.querySelector('[data-search-meta]')
const chromeEl = document.querySelector('.search-results__chrome-inner')
const breadcrumbsEl = document.querySelector('.search-results__breadcrumbs')

if (config.provider === 'algolia' && baseClient && pageInput && root) {
    const withAbsolutePositions = (hits, results) => {
        const page = Number(results?.page)
        const hitsPerPage = Number(results?.hitsPerPage)

        return hits.map((hit, index) => {
            if (Number.isInteger(hit.__position) && hit.__position > 0) return hit
            if (!Number.isInteger(page) || page < 0 || !Number.isInteger(hitsPerPage) || hitsPerPage < 1) return hit

            return {
                ...hit,
                __position: (page * hitsPerPage) + index + 1,
            }
        })
    }
    const sectionState = Object.fromEntries(SECTIONS.map(section => [section.id, {
        status: 'loading',
        nbHits: 0,
        error: false,
    }]))

    let currentQuery = new URLSearchParams(window.location.search).get('q') || ''
    let currentType = ''
    let searchInstance = null
    let craftFallbackStarted = false
    let activeMode = null
    let focusedWidgets = []
    let focusedState = { status: 'idle', nbHits: 0, error: false, nbPages: 0 }
    let userNavPending = false
    const lastPaint = Object.fromEntries(SECTIONS.map(section => [section.id, null]))

    const goCraftFallback = (query) => {
        if (craftFallbackStarted) return
        craftFallbackStarted = true
        window.location.replace(craftSearchUrl(query ?? currentQuery))
    }

    const isSearchStatusLoading = (status) => (
        status === 'loading' || status === 'stalled'
    )

    const isHitsLoading = (status, results) => (
        isSearchStatusLoading(status)
        || !results
        || Boolean(results.__isArtificial)
    )

    const escapeHtml = value => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')

    const decodeHtmlEntities = (value) => String(value ?? '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&')

    const safeHref = (url) => {
        if (typeof url !== 'string' || !url.trim()) return '#'
        try {
            const parsed = new URL(url.trim(), window.location.origin)
            if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.href
        } catch {
            // ignore invalid URLs
        }
        return '#'
    }

    const safeImageUrl = (url) => {
        if (typeof url !== 'string') return ''
        const trimmed = url.trim()
        if (!trimmed) return ''
        if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed
        try {
            const parsed = new URL(trimmed, window.location.origin)
            if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.href
        } catch {
            // ignore invalid URLs
        }
        return ''
    }

    const fallbackImageUrl = safeImageUrl(root.dataset.searchFallbackImage)

    const searchUrl = (query, type) => {
        const params = new URLSearchParams()
        if (query) params.set('q', query)
        if (type) params.set('type', type)
        const qs = params.toString()
        return qs ? `/search?${qs}` : '/search'
    }

    const getFocusedSection = (type) => {
        if (typeof type !== 'string' || !type) return null
        return FOCUSED_BY_ID[type] || null
    }

    const syncQuery = (next) => {
        const value = typeof next === 'string' ? next : ''
        if (value === currentQuery) return false
        currentQuery = value
        return true
    }

    const formatPrice = value => {
        if (value == null || value === '') return ''
        if (typeof value === 'string' && /\$/.test(value)) return value.trim()
        const amount = Number(String(value).replace(/[^0-9.-]/g, ''))
        if (!Number.isFinite(amount)) return ''
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount)
    }

    const HIGHLIGHT_TOKEN = /(<\/?(?:em|mark)>)/gi
    const appendHighlightedText = (parent, hit, attribute) => {
        parent.replaceChildren()
        const highlighted = hit?._highlightResult?.[attribute]?.value
        const fallback = String(hit?.[attribute] ?? '')
        if (typeof highlighted !== 'string' || !highlighted) {
            parent.appendChild(document.createTextNode(fallback))
            return
        }

        let openTag = null
        highlighted.split(HIGHLIGHT_TOKEN).forEach((part) => {
            if (!part) return
            const tagMatch = part.match(/^<\/?(em|mark)>$/i)
            if (tagMatch) {
                openTag = part[1] === '/' ? null : tagMatch[1].toLowerCase()
                return
            }
            const text = decodeHtmlEntities(part)
            if (openTag === 'em' || openTag === 'mark') {
                const wrap = document.createElement(openTag)
                wrap.textContent = text
                parent.appendChild(wrap)
            } else {
                parent.appendChild(document.createTextNode(text))
            }
        })
    }

    const labelValue = (value) => {
        if (typeof value === 'string' && value) return value
        if (value && typeof value === 'object' && typeof value.label === 'string') return value.label
        return ''
    }

    const humanizeKey = (value) => {
        const raw = labelValue(value)
        if (!raw) return ''
        if (raw === 'productType') return 'Product Type'
        if (raw === 'sport') return 'Sport'
        if (raw === 'article') return 'Article'
        if (raw === 'download') return 'Download'
        if (!/[a-z]/.test(raw) || !/[A-Z]/.test(raw)) {
            return raw
                .split(/[-_]/)
                .filter(Boolean)
                .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                .join(' ')
        }
        return raw.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase())
    }

    const eyebrowFor = (hit, section) => {
        if (section.product) {
            return labelValue(hit.primaryProductType)
                || labelValue(hit.productTypeLabels?.[0])
                || labelValue(hit.productTypes?.[0])
                || labelValue(hit.sportLabels?.[0])
        }
        if (section.id === 'resources') return humanizeKey(hit.resourceType)
        if (section.id === 'productTypeCategories' || section.id === 'sports') return ''
        if (section.id === 'projects') return 'Project'
        return humanizeKey(hit.collectionType) || humanizeKey(hit.kind)
    }

    const requestQuery = (request) => {
        const params = typeof request.params === 'string'
            ? Object.fromEntries(new URLSearchParams(request.params))
            : (request.params || {})
        return String(params.query || '').trim()
    }

    const emptyResultsPayload = requests => ({
        results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            page: 0,
            nbPages: 0,
            hitsPerPage: 0,
            exhaustiveNbHits: true,
            query: '',
            params: '',
            processingTimeMS: 0,
        })),
    })

    const wrapSearchClient = client => ({
        search(requests) {
            SECTIONS.forEach(section => {
                sectionState[section.id].error = false
            })
            focusedState.error = false

            if (requests.every(request => !requestQuery(request))) {
                SECTIONS.forEach(section => {
                    sectionState[section.id].status = 'idle'
                    sectionState[section.id].nbHits = 0
                    sectionState[section.id].error = false
                })
                focusedState = { status: 'idle', nbHits: 0, error: false, nbPages: 0 }
                return Promise.resolve(emptyResultsPayload(requests))
            }

            return client.search(requests).then(response => {
                const results = response.results || []
                results.forEach((result, i) => {
                    const request = requests[i] || {}
                    const params = typeof request.params === 'string'
                        ? Object.fromEntries(new URLSearchParams(request.params))
                        : (request.params || {})
                    const filters = params.filters || ''
                    const section = SECTIONS.find(item => item.filter === filters)
                    const failed = Boolean(result.status && result.status >= 400)
                        || Boolean(result.message && !Array.isArray(result.hits))
                    if (section) {
                        sectionState[section.id].error = failed
                    } else if (activeMode && activeMode !== 'overview') {
                        focusedState.error = failed
                    }
                })
                return response
            }).catch((error) => {
                if (error?.isGuardStale) return emptyResultsPayload(requests)

                SECTIONS.forEach(section => {
                    sectionState[section.id].error = true
                    sectionState[section.id].status = 'idle'
                    sectionState[section.id].nbHits = 0
                })
                focusedState = { status: 'idle', nbHits: 0, error: true, nbPages: 0 }
                updateMeta()
                updateGlobalStates()
                return emptyResultsPayload(requests)
            })
        },
    })

    const createEl = (tag, className) => {
        const node = document.createElement(tag)
        if (className) node.className = className
        return node
    }

    const createSkeletonCard = (section) => {
        const kind = section.card || (section.product ? 'product' : 'content')
        const card = createEl(
            'article',
            `search-results__card search-results__card--skeleton search-results__card--${kind}`,
        )
        card.setAttribute('aria-hidden', 'true')

        const skelTitle = () => {
            const title = createEl('div', 'search-results__skel-title')
            title.append(createEl('span'), createEl('span'))
            return title
        }

        if (kind === 'product') {
            const body = createEl('div', 'search-results__card-body')
            body.append(
                createEl('div', 'search-results__skel-image'),
                createEl('div', 'search-results__skel-brand'),
                skelTitle(),
            )
            const price = createEl('div', 'search-results__skel-price')
            price.append(createEl('span'), createEl('span'))
            card.append(body, price)
        } else if (kind === 'menu') {
            card.append(
                createEl('div', 'search-results__skel-image'),
                createEl('div', 'search-results__skel-menu-name'),
            )
        } else if (kind === 'sport') {
            card.append(
                createEl('div', 'search-results__skel-image'),
                createEl('div', 'search-results__skel-sport-name'),
            )
        } else {
            card.append(
                createEl('div', 'search-results__skel-image'),
                createEl('div', 'search-results__skel-tag'),
                skelTitle(),
            )
        }
        return card
    }

    const createImageBlock = (imageUrl, product) => {
        const wrap = createEl('div', 'search-results__card-image')
        if (!imageUrl && !fallbackImageUrl) {
            wrap.classList.add('search-results__card-image--empty')
            return wrap
        }
        const img = createEl('img')
        img.src = imageUrl || fallbackImageUrl
        img.alt = ''
        img.loading = 'lazy'
        img.width = 318
        img.height = product ? 186 : 184
        img.addEventListener('error', () => {
            if (fallbackImageUrl && img.getAttribute('src') !== fallbackImageUrl) {
                img.src = fallbackImageUrl
                return
            }
            wrap.classList.add('search-results__card-image--empty')
            img.remove()
        })
        wrap.appendChild(img)
        return wrap
    }

    const createCard = (hit, section) => {
        const protectedPage = hit.access === 'passwordProtected'
        const imageUrl = !protectedPage ? safeImageUrl(hit.image?.url) : ''
        const eyebrow = eyebrowFor(hit, section)
        const price = section.product && hit.startingPrice != null && hit.startingPrice !== ''
            ? formatPrice(hit.startingPrice)
            : ''
        const kind = section.card || (section.product ? 'product' : 'content')

        const card = createEl('a', `search-results__card search-results__card--${kind}`)
        card.href = safeHref(hit.url)
        card.dataset.tracking = buildSearchResultTracking(hit.kind)
        card.addEventListener('click', () => {
            trackResultClick({
                indexName: config.indexName,
                queryID: hit.__queryID,
                objectID: hit.objectID,
                position: hit.__position,
            })
        })

        const title = createEl('h3', 'search-results__card-title')
        if (protectedPage) {
            title.textContent = String(hit.title ?? '')
        } else {
            appendHighlightedText(title, hit, 'title')
        }

        if (kind === 'menu') {
            const imageBlock = createImageBlock(imageUrl, false)
            card.appendChild(imageBlock)
            card.appendChild(title)
            return card
        }

        if (kind === 'sport') {
            const imageBlock = createImageBlock(imageUrl, false)
            const name = createEl('div', 'search-results__card-sport-name')
            name.appendChild(title)
            card.append(imageBlock, name)
            return card
        }

        let eyebrowEl = null
        if (eyebrow) {
            eyebrowEl = createEl('p', 'search-results__card-eyebrow')
            eyebrowEl.textContent = eyebrow
        }

        const imageBlock = createImageBlock(imageUrl, section.product)

        if (kind === 'product') {
            const body = createEl('div', 'search-results__card-body')
            body.appendChild(imageBlock)
            if (eyebrowEl) body.appendChild(eyebrowEl)
            body.appendChild(title)
            card.appendChild(body)
            if (price) {
                const priceRow = createEl('div', 'search-results__card-price')
                const label = createEl('span', 'search-results__card-price-label')
                label.textContent = 'Starting Price'
                const value = createEl('span', 'search-results__card-price-value')
                value.textContent = price
                priceRow.append(label, value)
                card.appendChild(priceRow)
            }
        } else {
            card.appendChild(imageBlock)
            if (eyebrowEl) card.appendChild(eyebrowEl)
            card.appendChild(title)
        }

        return card
    }

    const renderSectionShell = (section) => {
        const el = document.createElement('section')
        el.className = 'search-results__section'
        el.dataset.searchSection = section.id
        el.hidden = true
        el.innerHTML = `
            <div class="search-results__section-inner">
                <div class="search-results__section-header">
                    <div class="search-results__section-heading">
                        <h2 class="search-results__section-title">${escapeHtml(section.label)}</h2>
                        <p class="search-results__section-badge">
                            <span data-section-badge>0</span>
                        </p>
                    </div>
                    <a class="search-results__view-all" data-section-view-all hidden href="#">View all</a>
                </div>
                <div class="search-results__grid" data-section-grid></div>
                <div class="search-results__section-error" data-section-error hidden>
                    <img class="search-results__state-graphic" src="/assets/img/search-error.svg" alt="" width="100" height="102">
                    <div class="search-results__state-copy">
                        <h3 class="search-results__state-title">We couldn't complete your search</h3>
                        <p class="search-results__state-text">Something went wrong while searching. Please try again.</p>
                    </div>
                    <button type="button" class="button search-results__retry" data-section-retry>Try again</button>
                </div>
            </div>
        `
        el.querySelector('[data-section-retry]')?.addEventListener('click', () => {
            searchInstance?.refresh()
        })
        return el
    }

    const zeroEl = document.createElement('section')
    zeroEl.className = 'search-results__state search-results__state--zero'
    zeroEl.hidden = true
    zeroEl.innerHTML = `
        <img class="search-results__state-graphic" src="/assets/img/search-no-results.svg" alt="" width="100" height="105">
        <div class="search-results__state-copy">
            <h2 class="search-results__state-title">No results found</h2>
            <p class="search-results__state-text">
                <span data-zero-line-1></span>
                <span>Try checking your spelling or using different keywords.</span>
            </p>
            <p class="search-results__state-help">
                <span>Need help? Contact our team or <a href="/products">browse all products</a>.</span>
                <span>You can also visit <a href="/resources">resources</a> or <a href="/contact">contact</a>.</span>
            </p>
        </div>
    `

    const errorEl = document.createElement('section')
    errorEl.className = 'search-results__state search-results__state--error'
    errorEl.hidden = true
    errorEl.innerHTML = `
        <img class="search-results__state-graphic" src="/assets/img/search-error.svg" alt="" width="100" height="102">
        <div class="search-results__state-body">
            <div class="search-results__state-copy">
                <h2 class="search-results__state-title">We couldn't complete your search</h2>
                <p class="search-results__state-text" data-error-line></p>
            </div>
            <button type="button" class="button search-results__retry" data-global-retry>Try again</button>
            <p class="search-results__state-help">
                Contact our team or <a href="/products">browse all products</a>.
            </p>
        </div>
    `
    errorEl.querySelector('[data-global-retry]')?.addEventListener('click', () => {
        searchInstance?.refresh()
    })

    const focusedEl = document.createElement('section')
    focusedEl.className = 'search-results__focused'
    focusedEl.hidden = true
    focusedEl.innerHTML = `
        <div class="search-results__focused-header">
            <div class="search-results__focused-inner">
                <div class="search-results__section-header">
                    <div class="search-results__section-heading">
                        <h2 class="search-results__section-title" data-focused-title tabindex="-1">Results</h2>
                        <p class="search-results__section-badge">
                            <span data-focused-badge>0</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div class="search-results__focused-body">
            <div class="search-results__focused-inner">
                <div class="search-results__focused-main">
                    <div class="search-results__grid" data-focused-grid></div>
                    <div class="search-results__pagination" data-focused-pagination hidden></div>
                    <div class="search-results__section-error" data-focused-error hidden>
                        <img class="search-results__state-graphic" src="/assets/img/search-error.svg" alt="" width="100" height="102">
                        <div class="search-results__state-copy">
                            <h3 class="search-results__state-title">We couldn't complete your search</h3>
                            <p class="search-results__state-text">Something went wrong while searching. Please try again.</p>
                        </div>
                        <button type="button" class="button search-results__retry" data-focused-retry>Try again</button>
                    </div>
                </div>
            </div>
        </div>
    `
    focusedEl.querySelector('[data-focused-retry]')?.addEventListener('click', () => {
        searchInstance?.refresh()
    })

    const statusEl = createEl('div', 'search-results__sr-status')
    statusEl.setAttribute('aria-live', 'polite')
    statusEl.setAttribute('aria-atomic', 'true')

    const backEl = createEl('a', 'search-results__back')
    backEl.hidden = true
    backEl.innerHTML = '<span class="search-results__back-chevron" aria-hidden="true"></span><span>Back</span>'
    backEl.addEventListener('click', () => {
        userNavPending = true
    })

    const breadcrumbTypeWrap = createEl('span', 'search-results__breadcrumbs-type')
    breadcrumbTypeWrap.hidden = true
    breadcrumbTypeWrap.innerHTML = `
        <span class="search-results__breadcrumbs-sep" aria-hidden="true">/</span>
        <span data-breadcrumb-type></span>
    `

    if (chromeEl) {
        chromeEl.insertBefore(backEl, chromeEl.firstChild)
    }
    if (breadcrumbsEl) {
        breadcrumbsEl.appendChild(breadcrumbTypeWrap)
    }
    root.appendChild(statusEl)

    const sectionEls = {}
    SECTIONS.forEach(section => {
        const el = renderSectionShell(section)
        sectionEls[section.id] = el
        root.appendChild(el)
    })
    root.appendChild(focusedEl)
    root.appendChild(zeroEl)
    root.appendChild(errorEl)

    const allSettled = () => {
        if (activeMode && activeMode !== 'overview') {
            return focusedState.status !== 'loading'
        }
        return SECTIONS.every(section => sectionState[section.id].status !== 'loading')
    }
    const anyError = () => {
        if (activeMode && activeMode !== 'overview') return focusedState.error
        return SECTIONS.some(section => sectionState[section.id].error)
    }
    const allErrors = () => {
        if (activeMode && activeMode !== 'overview') return focusedState.error
        return SECTIONS.every(section => sectionState[section.id].error)
    }
    const totalHits = () => {
        if (activeMode && activeMode !== 'overview') {
            return focusedState.error ? 0 : focusedState.nbHits
        }
        return SECTIONS.reduce((sum, section) => sum + (sectionState[section.id].error ? 0 : sectionState[section.id].nbHits), 0)
    }

    const announce = (message) => {
        if (statusEl) statusEl.textContent = message
    }

    const focusFocusedHeading = () => {
        if (!userNavPending) return
        userNavPending = false
        const heading = focusedEl.querySelector('[data-focused-title]')
        heading?.focus({ preventScroll: false })
    }

    const updateChrome = (section) => {
        const focused = Boolean(section)
        backEl.hidden = !focused
        backEl.href = searchUrl(currentQuery)
        breadcrumbTypeWrap.hidden = !focused
        const typeLabel = breadcrumbTypeWrap.querySelector('[data-breadcrumb-type]')
        if (typeLabel) typeLabel.textContent = section ? section.label : ''
        document.body.classList.toggle('search-results--focused', focused)
    }

    const updateMeta = () => {
        if (!metaEl) return
        const q = currentQuery.trim()
        const left = metaEl.querySelector('[data-meta-left]')
        const right = metaEl.querySelector('[data-meta-right]')
        const section = getFocusedSection(currentType)

        if (left) {
            if (!q) {
                left.textContent = 'Search our catalog'
            } else if (section) {
                left.textContent = `${section.label} for “${q}”`
            } else {
                left.textContent = `Results for “${q}”`
            }
        }
        if (!right) return
        if (!q) {
            right.textContent = ''
            return
        }
        if (!allSettled()) {
            right.textContent = 'Searching...'
            return
        }
        if (anyError() && totalHits() === 0) {
            right.textContent = 'No results'
            return
        }
        if (totalHits() === 0) {
            right.textContent = 'No results'
            return
        }
        const count = totalHits()
        right.textContent = count === 1 ? '1 result' : `${count} results`
    }

    const updateGlobalStates = () => {
        const q = currentQuery.trim()
        const settled = allSettled()
        const errors = anyError()
        const hits = totalHits()
        const focused = Boolean(getFocusedSection(currentType))

        const showGlobalError = settled && allErrors() && Boolean(q)
        const showZero = settled && !errors && q && hits === 0

        errorEl.hidden = !showGlobalError
        zeroEl.hidden = !showZero
        if (showZero) {
            const line = zeroEl.querySelector('[data-zero-line-1]')
            if (line) line.textContent = `We couldn’t find matches for “${q}”.`
        }
        if (showGlobalError) {
            const line = errorEl.querySelector('[data-error-line]')
            if (line) {
                line.textContent = `Something went wrong while searching for “${q}”. Your search is still in the field — please try again.`
            }
        }

        if (!q || showGlobalError || showZero) {
            SECTIONS.forEach(section => {
                sectionEls[section.id].hidden = true
            })
            if (showGlobalError || showZero) {
                focusedEl.hidden = true
            }
        }

        if (settled && q && !showGlobalError && !showZero) {
            announce(hits === 1 ? '1 result' : `${hits} results`)
            if (focused) focusFocusedHeading()
        }

        applySectionTones()
    }

    const applySectionTones = () => {
        let visibleIndex = 0
        SECTIONS.forEach(section => {
            const el = sectionEls[section.id]
            el.classList.remove('search-results__section--grey', 'search-results__section--light')
            if (el.hidden) return
            const tone = visibleIndex % 2 === 0 ? 'light' : 'grey'
            el.classList.add(`search-results__section--${tone}`)
            visibleIndex += 1
        })
    }

    const paintSection = (section, payload) => {
        lastPaint[section.id] = payload
        if (activeMode && activeMode !== 'overview') {
            sectionEls[section.id].hidden = true
            return
        }

        const { hits, nbHits, loading } = payload
        const el = sectionEls[section.id]
        const grid = el.querySelector('[data-section-grid]')
        const badge = el.querySelector('[data-section-badge]')
        const viewAll = el.querySelector('[data-section-view-all]')
        const err = el.querySelector('[data-section-error]')
        const state = sectionState[section.id]
        const q = currentQuery.trim()
        const showGlobalError = allSettled() && allErrors()
        const showZero = allSettled() && !anyError() && q && totalHits() === 0

        if (!q || showGlobalError || showZero) {
            el.hidden = true
            return
        }

        if (state.error) {
            el.hidden = false
            grid.hidden = true
            err.hidden = false
            if (badge) badge.textContent = '!'
            if (viewAll) viewAll.hidden = true
            return
        }

        err.hidden = true
        grid.hidden = false

        if (loading) {
            if (section.card === 'menu' || section.card === 'sport') {
                el.hidden = true
                grid.replaceChildren()
                return
            }
            el.hidden = false
            if (badge) badge.textContent = '…'
            if (viewAll) viewAll.hidden = true
            grid.replaceChildren(
                ...Array.from({ length: section.hitsPerPage }, () => createSkeletonCard(section)),
            )
            return
        }

        if (!nbHits) {
            el.hidden = true
            grid.replaceChildren()
            if (badge) badge.textContent = '0'
            if (viewAll) viewAll.hidden = true
            return
        }

        el.hidden = false
        if (badge) badge.textContent = String(nbHits)
        if (viewAll) {
            const showViewAll = section.focused && nbHits > section.hitsPerPage
            viewAll.hidden = !showViewAll
            if (showViewAll) {
                viewAll.href = searchUrl(currentQuery, section.id)
                viewAll.textContent = `View all ${nbHits} ${section.label.toLowerCase()}`
                viewAll.addEventListener('click', () => {
                    userNavPending = true
                }, { once: true })
            }
        }
        grid.replaceChildren(...hits.map(hit => createCard(hit, section)))
    }

    const paintFocused = (section, payload) => {
        const { hits, nbHits, loading, nbPages } = payload
        const grid = focusedEl.querySelector('[data-focused-grid]')
        const badge = focusedEl.querySelector('[data-focused-badge]')
        const title = focusedEl.querySelector('[data-focused-title]')
        const err = focusedEl.querySelector('[data-focused-error]')
        const pager = focusedEl.querySelector('[data-focused-pagination]')
        const q = currentQuery.trim()

        title.textContent = section.label
        focusedEl.dataset.focusedCard = section.card || (section.product ? 'product' : 'content')
        updateChrome(section)

        if (!q) {
            focusedEl.hidden = true
            return
        }

        focusedEl.hidden = false
        SECTIONS.forEach(item => {
            sectionEls[item.id].hidden = true
        })

        if (focusedState.error) {
            grid.hidden = true
            pager.hidden = true
            err.hidden = false
            if (badge) badge.textContent = '!'
            return
        }

        err.hidden = true
        grid.hidden = false

        if (loading) {
            if (badge) badge.textContent = '…'
            pager.hidden = true
            grid.replaceChildren(
                ...Array.from({ length: section.focusedHitsPerPage }, () => createSkeletonCard(section)),
            )
            return
        }

        if (badge) badge.textContent = String(nbHits)
        pager.hidden = !(nbPages > 1)
        if (!nbHits) {
            grid.replaceChildren()
            return
        }
        grid.replaceChildren(...hits.map(hit => createCard(hit, section)))
    }

    const repaintAllSections = () => {
        SECTIONS.forEach(section => {
            const payload = lastPaint[section.id]
            if (payload) paintSection(section, payload)
        })
    }

    const createSectionWidgets = (section) => {
        const hitsWidget = connectHits((renderOptions) => {
            const { hits, results } = renderOptions
            if (results && typeof results.query === 'string') {
                syncQuery(results.query)
            }
            const loading = !results || Boolean(results.__isArtificial)
            const nbHits = results?.nbHits ?? 0

            if (loading) {
                sectionState[section.id].status = 'loading'
            } else {
                sectionState[section.id].status = 'idle'
                sectionState[section.id].nbHits = sectionState[section.id].error ? 0 : nbHits
            }

            paintSection(section, {
                hits: withAbsolutePositions(hits, results),
                nbHits,
                loading: loading && !sectionState[section.id].error,
            })
            updateMeta()
            updateGlobalStates()
            applySectionTones()
        })

        const configureParams = {
            filters: section.filter,
            hitsPerPage: section.hitsPerPage,
            attributesToRetrieve: section.retrieve,
            attributesToHighlight: ['title', 'summary'],
            attributesToSnippet: ['summary:24'],
            clickAnalytics: true,
            analyticsTags: ['results-page'],
        }
        if (section.restrictSearchableAttributes?.length) {
            configureParams.restrictSearchableAttributes = section.restrictSearchableAttributes
        }

        return [
            configure(configureParams),
            hitsWidget({}),
        ]
    }

    const createFocusedWidgets = (section) => {
        const hitsWidget = connectHits((renderOptions) => {
            const { hits, results, instantSearchInstance } = renderOptions
            if (results && typeof results.query === 'string') {
                syncQuery(results.query)
            }
            const loading = isHitsLoading(instantSearchInstance?.status, results)
            const nbHits = results?.nbHits ?? 0
            const nbPages = results?.nbPages ?? 0

            if (loading) {
                focusedState.status = 'loading'
            } else {
                focusedState.status = 'idle'
                focusedState.nbHits = focusedState.error ? 0 : nbHits
                focusedState.nbPages = nbPages
            }

            paintFocused(section, {
                hits: withAbsolutePositions(hits, results),
                nbHits,
                nbPages,
                loading: loading && !focusedState.error,
            })
            updateMeta()
            updateGlobalStates()
        })

        const pagerMount = focusedEl.querySelector('[data-focused-pagination]')
        pagerMount.addEventListener('click', () => {
            userNavPending = true
        })

        const configureParams = {
            filters: section.filter,
            hitsPerPage: section.focusedHitsPerPage,
            attributesToRetrieve: section.retrieve,
            attributesToHighlight: ['title', 'summary'],
            attributesToSnippet: ['summary:24'],
            clickAnalytics: true,
            analyticsTags: ['results-page'],
        }
        if (section.restrictSearchableAttributes?.length) {
            configureParams.restrictSearchableAttributes = section.restrictSearchableAttributes
        }

        return [
            configure(configureParams),
            hitsWidget({}),
            pagination({
                container: pagerMount,
                padding: 2,
                showFirst: false,
                showLast: false,
                cssClasses: {
                    root: 'search-results__pagination-root',
                    noRefinementRoot: 'search-results__pagination-root--empty',
                    list: 'search-results__pagination-list',
                    item: 'search-results__pagination-item',
                    selectedItem: 'search-results__pagination-item--selected',
                    disabledItem: 'search-results__pagination-item--disabled',
                    previousPageItem: 'search-results__pagination-item--previous',
                    nextPageItem: 'search-results__pagination-item--next',
                    pageItem: 'search-results__pagination-item--page',
                    link: 'search-results__pagination-link',
                },
                templates: {
                    previous: '<span aria-hidden="true"><svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.10059 8.27978C4.83309 8.56929 4.38159 8.58699 4.0918 8.31982L0.289063 4.81006L0.268555 4.78955C0.267087 4.78808 0.266103 4.78615 0.264649 4.78467C0.236203 4.75571 0.210373 4.72484 0.1875 4.69189C0.182389 4.68455 0.177692 4.67697 0.172852 4.66943C0.151883 4.63666 0.133485 4.60254 0.118164 4.56689C0.116082 4.56206 0.114284 4.55713 0.112305 4.55225C0.0968412 4.51395 0.0838503 4.47467 0.0751957 4.43408C0.0741743 4.42927 0.0741667 4.42427 0.0732425 4.41943C0.0648378 4.3756 0.0596155 4.33097 0.0595707 4.28564C0.0595707 4.24054 0.0639957 4.19554 0.072266 4.15185C0.0731319 4.14726 0.074242 4.14274 0.0751957 4.13818C0.0837797 4.09724 0.0968145 4.05769 0.112305 4.01904C0.114276 4.01415 0.116088 4.00923 0.118164 4.00439C0.134545 3.966 0.153864 3.92909 0.176758 3.89404C0.178594 3.89125 0.179767 3.88802 0.181641 3.88525C0.204963 3.85063 0.232334 3.81889 0.261719 3.78857C0.26638 3.78377 0.271574 3.77957 0.276368 3.7749C0.280975 3.77041 0.284322 3.76463 0.289063 3.76025L4.0918 0.250488C4.38154 -0.0169224 4.83298 0.00104199 5.10059 0.290527C5.36782 0.580413 5.3503 1.03283 5.06055 1.30029L2.60156 3.5708L8.37891 3.5708C8.77313 3.57101 9.09262 3.89044 9.09277 4.28467C9.09277 4.67903 8.77322 4.99833 8.37891 4.99854L2.59961 4.99853L5.06055 7.271C5.34994 7.53863 5.368 7.99008 5.10059 8.27978Z" fill="white"/></svg></span> <span class="search-results__pagination-label">Previous</span>',
                    next: '<span class="search-results__pagination-label">Next</span> <span aria-hidden="true"><svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.99219 0.229492C4.25969 -0.0600085 4.71118 -0.0777086 5.00098 0.189453L8.80371 3.69922L8.82422 3.71973C8.82569 3.72119 8.82667 3.72313 8.82812 3.72461C8.85657 3.75357 8.8824 3.78443 8.90527 3.81738C8.91039 3.82472 8.91508 3.83231 8.91992 3.83984C8.94089 3.87262 8.95929 3.90674 8.97461 3.94238C8.97669 3.94722 8.97849 3.95214 8.98047 3.95703C8.99593 3.99532 9.00892 4.03461 9.01758 4.0752C9.0186 4.08 9.01861 4.085 9.01953 4.08984C9.02794 4.13368 9.03316 4.17831 9.0332 4.22363C9.0332 4.26873 9.02878 4.31374 9.02051 4.35742C9.01964 4.36201 9.01853 4.36653 9.01758 4.37109C9.00899 4.41204 8.99596 4.45159 8.98047 4.49023C8.9785 4.49513 8.97669 4.50004 8.97461 4.50488C8.95823 4.54327 8.93891 4.58019 8.91602 4.61523C8.91418 4.61803 8.91301 4.62126 8.91113 4.62402C8.88781 4.65865 8.86044 4.69038 8.83105 4.7207C8.82639 4.7255 8.8212 4.72971 8.81641 4.73438C8.8118 4.73887 8.80845 4.74465 8.80371 4.74902L5.00098 8.25879C4.71123 8.5262 4.25979 8.50823 3.99219 8.21875C3.72495 7.92886 3.74247 7.47645 4.03223 7.20898L6.49121 4.93848H0.713867C0.319648 4.93827 0.000149359 4.61884 0 4.22461C0 3.83025 0.319556 3.51095 0.713867 3.51074H6.49316L4.03223 1.23828C3.74284 0.970649 3.72477 0.519196 3.99219 0.229492Z" fill="white"/></svg></span>',
                },
            }),
        ]
    }

    const indexName = config.indexName
    const guardedClient = createGuardedSearchClient(baseClient, {
        onFailure: ({ query }) => {
            goCraftFallback(query || currentQuery)
        },
    })
    const searchClient = wrapSearchClient(guardedClient)

    const startInstantSearch = () => {
    const searchBoxWidget = searchBox({
        container: pageInput,
        placeholder: 'Search',
        showReset: false,
        showLoadingIndicator: false,
        cssClasses: {
            root: 'search-results__searchbox',
            form: 'search-results__search-form',
            input: 'search-results__search-input',
            submit: 'search-results__search-submit',
        },
        templates: {
            submit() {
                return `<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <g clip-path="url(#clip0_search_submit)">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.1723 14.8705C11.4476 16.0351 9.34191 16.494 7.289 16.1525C5.2361 15.8111 3.39231 14.6953 2.13742 13.0351C0.882532 11.3749 0.312115 9.29671 0.543664 7.22853C0.775213 5.16035 1.79109 3.25971 3.38205 1.91813C4.97301 0.576548 7.01789 -0.103817 9.09548 0.0171809C11.1731 0.138178 13.1251 1.05132 14.5496 2.56852C15.9741 4.08572 16.7624 6.09143 16.7523 8.17252C16.7422 10.2536 15.9344 12.2516 14.4953 13.7549L14.5373 13.7942L20.2493 19.5062C20.3339 19.5868 20.4014 19.6834 20.4481 19.7904C20.4947 19.8975 20.5195 20.0127 20.5209 20.1295C20.5223 20.2463 20.5004 20.3621 20.4564 20.4702C20.4124 20.5784 20.3471 20.6766 20.2646 20.7592C20.182 20.8418 20.0838 20.907 19.9756 20.951C19.8675 20.995 19.7516 21.017 19.6349 21.0155C19.5181 21.0141 19.4028 20.9894 19.2958 20.9427C19.1888 20.8961 19.0921 20.8285 19.0116 20.7439L13.2996 15.0319C13.2509 14.9834 13.2082 14.9292 13.1723 14.8705ZM15.3563 8.12948C15.3563 9.91452 14.6472 11.6265 13.385 12.8887C12.1228 14.1509 10.4108 14.86 8.6258 14.86C6.84076 14.86 5.12883 14.1509 3.86662 12.8887C2.6044 11.6265 1.8953 9.91452 1.8953 8.12948C1.8953 6.34444 2.6044 4.63252 3.86662 3.3703C5.12883 2.10809 6.84076 1.39898 8.6258 1.39898C10.4108 1.39898 12.1228 2.10809 13.385 3.3703C14.6472 4.63252 15.3563 6.34444 15.3563 8.12948Z" fill="white"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_search_submit">
                            <rect width="21" height="21" fill="white"/>
                        </clipPath>
                    </defs>
                </svg>`
            },
        },
    })

    const overviewRootConfigure = configure({
        hitsPerPage: 0,
        analyticsTags: ['results-page'],
    })

    const overviewIndexWidgets = SECTIONS.map(section => index({
        indexName,
        indexId: section.id,
    }).addWidgets(createSectionWidgets(section)))

    const applyMode = (type) => {
        const section = getFocusedSection(type)
        const nextMode = section ? section.id : 'overview'
        currentType = section ? section.id : ''

        if (nextMode === activeMode) {
            updateChrome(section)
            return
        }

        if (activeMode === 'overview') {
            searchInstance.removeWidgets([overviewRootConfigure, ...overviewIndexWidgets])
        } else if (activeMode) {
            searchInstance.removeWidgets(focusedWidgets)
            focusedWidgets = []
        }

        if (section) {
            focusedState = { status: 'loading', nbHits: 0, error: false, nbPages: 0 }
            focusedWidgets = createFocusedWidgets(section)
            focusedEl.hidden = false
            SECTIONS.forEach(item => {
                sectionEls[item.id].hidden = true
            })
            zeroEl.hidden = true
            errorEl.hidden = true
            paintFocused(section, {
                hits: [],
                nbHits: 0,
                nbPages: 0,
                loading: true,
            })
            searchInstance.addWidgets(focusedWidgets)
        } else {
            searchInstance.addWidgets([overviewRootConfigure, ...overviewIndexWidgets])
            focusedEl.hidden = true
            updateChrome(null)
        }

        activeMode = nextMode
        updateChrome(section)
        updateMeta()
    }

    searchInstance = instantsearch({
        indexName,
        searchClient,
        routing: {
            router: historyRouter({
                cleanUrlOnDispose: false,
                createURL({ qsModule, routeState, location }) {
                    const { origin, pathname, hash } = location
                    const existing = qsModule.parse(location.search.slice(1))
                    const queryParameters = { ...existing }
                    ;['q', 'type', 'productType', 'sport', 'resourceType', 'page'].forEach((key) => {
                        delete queryParameters[key]
                    })

                    if (routeState.q) queryParameters.q = routeState.q
                    if (routeState.type) queryParameters.type = routeState.type

                    const focused = getFocusedSection(routeState.type)
                    if (focused && routeState.page && routeState.page > 1) {
                        queryParameters.page = routeState.page
                    }

                    const queryString = qsModule.stringify(queryParameters, { arrayFormat: 'repeat' })
                    return `${origin}${pathname}${queryString ? `?${queryString}` : ''}${hash}`
                },
                parseURL({ qsModule, location }) {
                    const parsed = qsModule.parse(location.search.slice(1), { arrayFormat: 'repeat' })
                    const type = typeof parsed.type === 'string' ? parsed.type : ''
                    const focused = getFocusedSection(type)
                    return {
                        q: typeof parsed.q === 'string' ? parsed.q : '',
                        type: focused ? focused.id : '',
                        page: Math.max(1, parseInt(parsed.page, 10) || 1),
                    }
                },
            }),
            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[indexName] || {}
                    const focused = getFocusedSection(currentType)
                    return {
                        q: indexUiState.query || '',
                        type: focused ? focused.id : '',
                        page: focused ? Math.max(1, indexUiState.page || 1) : 1,
                    }
                },
                routeToState(routeState) {
                    const focused = getFocusedSection(routeState.type)
                    currentType = focused ? focused.id : ''
                    return {
                        [indexName]: {
                            query: routeState.q || '',
                            page: focused ? Math.max(1, routeState.page || 1) : 1,
                        },
                    }
                },
            },
        },
    })

    const initialType = new URLSearchParams(window.location.search).get('type') || ''
    const initialSection = getFocusedSection(initialType)
    currentType = initialSection ? initialSection.id : ''

    if (initialSection) {
        focusedState = { status: 'loading', nbHits: 0, error: false, nbPages: 0 }
        focusedWidgets = createFocusedWidgets(initialSection)
        searchInstance.addWidgets([searchBoxWidget, ...focusedWidgets])
        activeMode = initialSection.id
        updateChrome(initialSection)
        paintFocused(initialSection, {
            hits: [],
            nbHits: 0,
            nbPages: 0,
            loading: true,
        })
    } else {
        searchInstance.addWidgets([searchBoxWidget, overviewRootConfigure, ...overviewIndexWidgets])
        activeMode = 'overview'
        updateChrome(null)
        SECTIONS.forEach((section) => {
            sectionState[section.id].status = 'loading'
            paintSection(section, { hits: [], nbHits: 0, loading: true })
        })
        applySectionTones()
    }

    searchInstance.on('render', () => {
        const boxState = searchInstance.renderState?.[indexName]?.searchBox
        const nextQuery = boxState?.query
            ?? searchInstance.helper?.state?.query
            ?? currentQuery
        const changed = syncQuery(nextQuery)

        const urlType = new URLSearchParams(window.location.search).get('type') || ''
        const urlFocusedId = getFocusedSection(urlType)?.id || ''
        const activeFocusedId = activeMode === 'overview' ? '' : activeMode
        if (urlFocusedId !== activeFocusedId) {
            applyMode(urlType)
        }

        if (changed && activeMode === 'overview') repaintAllSections()
        updateMeta()
        updateGlobalStates()
    })

    searchInstance.start()
    }

    const bootstrap = async () => {
        if (!currentQuery.trim()) {
            try {
                const { empty } = await probeEmptyIndex(guardedClient, indexName)
                if (craftFallbackStarted) return
                if (empty) {
                    goCraftFallback('')
                    return
                }
            } catch {
                if (!craftFallbackStarted) goCraftFallback('')
                return
            }
        }

        if (craftFallbackStarted) return
        startInstantSearch()
    }

    bootstrap()
}
